import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex gap-x-1 flex-wrap mb-2 w-full justify-center" }
const _hoisted_2 = ["onClick", "onMouseover"]
const _hoisted_3 = { class: "w-12 h-12 rounded-2xl flex justify-center items-start overflow-hidden" }
const _hoisted_4 = ["src", "srcset"]
const _hoisted_5 = { class: "text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1" }
const _hoisted_6 = { class: "flex gap-x-1 flex-wrap w-full justify-center" }
const _hoisted_7 = ["onClick", "onMouseover"]
const _hoisted_8 = { class: "w-12 h-12 rounded-2xl flex justify-center items-start overflow-hidden" }
const _hoisted_9 = ["src", "srcset"]
const _hoisted_10 = { class: "text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1" }

import ItemV3ParameterBox from "@/components/itemV3/parameter/itemV3ParameterBox.vue";
import ItemV3MostSelected from "@/components/itemV3/parameter/itemV3MostSelected.vue";
import {computed, inject, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";
import {useI18n} from "vue-i18n";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import {getCurrentText} from "@/assets/ts/itemText";

const imageWidthPercent: number = 30
const boxIdx = "profile"

const variantIdx = "profile"

const focusValueIdxCount = 5


export default /*@__PURE__*/_defineComponent({
  __name: 'itemV3Profile',
  props: ["item"],
  emits: ["patch"],
  setup(__props, { emit: __emit }) {


const {t} = useI18n();

const emit = __emit;
const props = __props;

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

const loading = ref(true)

/**
 * Parameter box header images
 */

const mainMetal = computed(() => store.getters["session/getMainMetal"])
const getHeaderImage = (size: string = "600x300", withBg = false) => {
  if (withBg) {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${mainMetal.value}_${boxIdx}.webp`
  } else {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${boxIdx}.webp`
  }
}

/**
 * session
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

const open = ref(false)

const variantList = computed(() => open.value ? parametersVariations.getVariants() : [])

/**
 * color variants
 */

const activeIdx = ref(0)

const variants = computed(() => {
  if (!variantList.value.length || variantList.value.length < activeIdx.value) return []
  return variantList.value[activeIdx.value].values
})

const active = computed(() => {
  if (!variantList.value.length || variantList.value.length < activeIdx.value) return []
  return variantList.value[activeIdx.value]
})

const matchedIdx = computed(() => {
  if (!variantList.value.length || variantList.value.length < activeIdx.value) return -1
  return variantList.value[activeIdx.value].values.findIndex((v: any) => v.match)
})

const currentProfileText = computed(() => {
  return getCurrentText(configureAll.value ? -1 : itemIdx.value, variantIdx)
})

/**
 * image
 */

const hoverProfileIdx = ref(-1)

const setHoverProfileIdx = () => {
  if (matchedIdx.value !== -1 && variants.value.length >= matchedIdx.value) {
    hoverProfileIdx.value = matchedIdx.value
  }
}

watch(active, () => {
  setHoverProfileIdx()
})

watch(props.item, () => {
  setHoverProfileIdx()
})

const imageUrl = computed(() => parametersVariations.imageUrl(variants.value, hoverProfileIdx.value))

const getProfileImageUrl = (profile: string, color: string) => {
  if (!profile || !color) return ''
  return 'https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/cross_sections/' + profile.toLowerCase() + '/' + profile.toLowerCase() + '_30_20_' + color.toLowerCase() + '.webp'
}

const checkImageLoaded = (url: string) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(true);
    img.src = url;
  });
};

const checkAllLoaded = async () => {
  const profiles = variantList.value;

  if (!profiles.length) return;
  const allListsCreated = profiles.every((profile: any) => profile.values && profile.values.length > 0);
  if (!allListsCreated) return;

  const imageUrls = profiles.flatMap((disc: any) => disc.values.map((profile: any) => getProfileImageUrl(profile.idx, mainMetal.value)));
  await Promise.all(imageUrls.map((url: string) => checkImageLoaded(url)));

  loading.value = false;
};

watch(variantList, () => {
  checkAllLoaded();
  if (variantList.value.length > 0) {
    loading.value = true;
  }
}, {immediate: true, deep: true});

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  emit('patch', {
    action: actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};

/**
 * tip texts
 */

const getProfileTipTitle = computed(() => {
  if (active.value && active.value.values[hoverProfileIdx.value]) {
    const key = active.value.values[hoverProfileIdx.value].idx.toUpperCase()
    return t('components.parameters.tips.profiles.' + key + '.title')
  } else {
    return ''
  }
})

const getProfileTipText = computed(() => {
  if (active.value && active.value.values[hoverProfileIdx.value]) {
    const key = active.value.values[hoverProfileIdx.value].idx.toUpperCase()
    return t('components.parameters.tips.profiles.' + key + '.text')
  } else {
    return ''
  }
})

const getProfileTipImageUrl = computed(() => {
  if (active.value && active.value.values[hoverProfileIdx.value]) {
    const key = active.value.values[hoverProfileIdx.value].idx.toLowerCase()
    return "https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/tips/profile/600x600/" + key + "_30_20_yellow.webp"
  } else {
    return ''
  }
})

onMounted(() => {
      setHoverProfileIdx()
    }
)

/*
 * focus values
 */

const focusValueIdx = [
  "RK", "RL", "RM", "RP", "RT"
]

const getFocusValueIdx = computed(
    () => {
      let focusList = focusValueIdx

      if (matchedIdx.value) {
        if (!focusValueIdx.includes(variantList.value[activeIdx.value].values[matchedIdx.value].idx)) {
          focusList = [
            variantList.value[activeIdx.value].values[matchedIdx.value].idx,
            ...focusValueIdx.slice(0, -1)
          ]
        }
      }

      const availableFocus = variants.value
          .map((v: any) => v.idx.toString())
          .filter((idx: string) => focusList.includes(idx))

      if (availableFocus.length < focusValueIdxCount) {
        const nextNonFocus = variants.value
            .map((v: any) => v.idx.toString())
            .filter((idx: string) => !availableFocus.includes(idx))

        availableFocus.push(...nextNonFocus.slice(0, focusValueIdxCount - availableFocus.length))
      }

      return availableFocus
    }
)



return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ItemV3ParameterBox, {
    open: open.value,
    "onUpdate:open": _cache[2] || (_cache[2] = ($event: any) => ((open).value = $event)),
    "image-width-percent": imageWidthPercent,
    image: imageUrl.value,
    "header-image": getHeaderImage(),
    "header-image-srcset": getHeaderImage() + ' 300w, ' + getHeaderImage('1200x600') +' 600w',
    title: "Profile",
    subtitle: currentProfileText.value || 'Wählen Sie die Profilform für Ihre Trauringe',
    loading: loading.value,
    "tip-title": getProfileTipTitle.value,
    "tip-text": getProfileTipText.value,
    "tip-image": getProfileTipImageUrl.value
  }, {
    main: _withCtx(() => [
      _createVNode(ItemV3MostSelected),
      _createElementVNode("ul", _hoisted_1, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(variants.value, (profile, profileIdx) => {
          return (_openBlock(), _createElementBlock(_Fragment, null, [
            (getFocusValueIdx.value.includes(profile.idx))
              ? (_openBlock(), _createElementBlock("li", {
                  key: profileIdx,
                  class: _normalizeClass(["cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]", {
              'border-2 border-brand-main bg-[#f3f0e9]': profile.match,
              'border-2 border-transparent': !profile.match
            }]),
                  onClick: ($event: any) => (handleValuePatch(active.value.idx, profile)),
                  onMouseover: ($event: any) => (hoverProfileIdx.value = profileIdx as number),
                  onMouseleave: _cache[0] || (_cache[0] = ($event: any) => (hoverProfileIdx.value = matchedIdx.value))
                }, [
                  _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("img", {
                      src: getProfileImageUrl(profile.idx, mainMetal.value),
                      srcset: getProfileImageUrl(profile.idx, mainMetal.value),
                      class: "relative h-60 -top-22 object-cover"
                    }, null, 8, _hoisted_4)
                  ]),
                  _createElementVNode("span", _hoisted_5, _toDisplayString(profile.idx), 1)
                ], 42, _hoisted_2))
              : _createCommentVNode("", true)
          ], 64))
        }), 256))
      ])
    ]),
    more: _withCtx(() => [
      _createElementVNode("ul", _hoisted_6, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(variants.value, (profile, profileIdx) => {
          return (_openBlock(), _createElementBlock(_Fragment, null, [
            (!getFocusValueIdx.value.includes(profile.idx))
              ? (_openBlock(), _createElementBlock("li", {
                  key: profileIdx,
                  class: _normalizeClass(["cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]", {
              'border-2 border-brand-main bg-[#f3f0e9]': profile.match,
              'border-2 border-transparent': !profile.match
            }]),
                  onClick: ($event: any) => (handleValuePatch(active.value.idx, profile)),
                  onMouseover: ($event: any) => (hoverProfileIdx.value = profileIdx as number),
                  onMouseleave: _cache[1] || (_cache[1] = ($event: any) => (hoverProfileIdx.value = matchedIdx.value))
                }, [
                  _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("img", {
                      src: getProfileImageUrl(profile.idx, mainMetal.value),
                      srcset: getProfileImageUrl(profile.idx, mainMetal.value),
                      class: "relative h-60 -top-22 object-cover"
                    }, null, 8, _hoisted_9)
                  ]),
                  _createElementVNode("span", _hoisted_10, _toDisplayString(profile.idx), 1)
                ], 42, _hoisted_7))
              : _createCommentVNode("", true)
          ], 64))
        }), 256))
      ])
    ]),
    _: 1
  }, 8, ["open", "image", "header-image", "header-image-srcset", "subtitle", "loading", "tip-title", "tip-text", "tip-image"]))
}
}

})